import { createContext, useContext, useState } from 'react';
import { orderBy, shuffle } from 'es-toolkit';
import type { QuizSetData } from '@/api/gen/_model/quizSetData';
import { type QuizWorksheetExportProps } from '../type';
import { quizListDataAdapter } from '../util/quizListDataAdapter';
import { sortQuizChoice } from '../util/sortQuizChoice';
type AdditionalSettingType = {
  addJoinCode: boolean;
  randomQuestionOrder: boolean;
  randomChoiceOrder: boolean;
};
const DEFAULT_ADDITIONAL_SETTING: AdditionalSettingType = {
  addJoinCode: true,
  randomQuestionOrder: false,
  randomChoiceOrder: false
};
type WorksheetSettingContextType = {
  additionalSetting: AdditionalSettingType;
  handleAdditionalSettingToggle: (key: keyof AdditionalSettingType) => void;
  worksheetTitle: string;
  worksheetTitleDefault: string;
  worksheetTeacherName: string;
  worksheetTeacherNameDefault: string;
  handleWorksheetTitleChange: (title: string) => void;
  handleWorksheetTeacherNameChange: (teacherName: string) => void;
  category: string;
  quizList: QuizSetData[];
  entryMapHashId: string;
  enterCode: number;
  shouldIncludeAnswerPage: boolean;
  handleShouldIncludeAnswerPageToggle: () => void;
};
const worksheetSettingContext = createContext<WorksheetSettingContextType>({
  additionalSetting: DEFAULT_ADDITIONAL_SETTING,
  handleAdditionalSettingToggle: () => {},
  worksheetTitle: '',
  worksheetTitleDefault: '',
  worksheetTeacherName: '',
  worksheetTeacherNameDefault: '',
  handleWorksheetTitleChange: () => {},
  handleWorksheetTeacherNameChange: () => {},
  category: '',
  quizList: [],
  entryMapHashId: '',
  enterCode: 0,
  shouldIncludeAnswerPage: true,
  handleShouldIncludeAnswerPageToggle: () => {}
});
export const WorksheetSettingContext = ({
  children,
  props
}: {
  children: React.ReactNode;
  props: QuizWorksheetExportProps;
}) => {
  const [quizList, setQuizList] = useState(() => quizListDataAdapter(props.quizList));
  const [additionalSetting, setAdditionalSetting] = useState(DEFAULT_ADDITIONAL_SETTING);
  const [worksheetTitle, setWorksheetTitle] = useState(props.defaultTitle);
  const [worksheetTeacherName, setWorksheetTeacherName] = useState(props.defaultTeacherName);
  const [shouldIncludeAnswerPage, setShouldIncludeAnswerPage] = useState(true);
  const handleShouldIncludeAnswerPageToggle = () => {
    setShouldIncludeAnswerPage(!shouldIncludeAnswerPage);
  };
  const handleWorksheetTitleChange = (titleInput: string) => {
    if (!titleInput.trim()) setWorksheetTitle(props.defaultTitle);else setWorksheetTitle(titleInput);
  };
  const handleWorksheetTeacherNameChange = (teacherNameInput: string) => {
    if (!teacherNameInput.trim()) setWorksheetTeacherName(props.defaultTeacherName);else setWorksheetTeacherName(teacherNameInput);
  };
  const handleAdditionalSettingToggle = (key: keyof AdditionalSettingType) => {
    const newAdditionalSetting = {
      ...additionalSetting,
      [key]: !additionalSetting[key]
    };
    switch (key) {
      case 'randomQuestionOrder':
        if (newAdditionalSetting.randomQuestionOrder) {
          setQuizList(shuffle(quizList));
        } else {
          setQuizList(orderBy(quizList, ['order'], ['asc']));
        }
        break;
      case 'randomChoiceOrder':
        setQuizList(sortQuizChoice(quizList, newAdditionalSetting.randomChoiceOrder));
        break;
      case 'addJoinCode':
        break;
    }
    setAdditionalSetting(newAdditionalSetting);
  };
  return <worksheetSettingContext.Provider value={{
    additionalSetting,
    handleAdditionalSettingToggle,
    worksheetTitle,
    worksheetTitleDefault: props.defaultTitle,
    worksheetTeacherName,
    worksheetTeacherNameDefault: props.defaultTeacherName,
    handleWorksheetTitleChange,
    handleWorksheetTeacherNameChange,
    category: props.category,
    quizList,
    entryMapHashId: props.entryMapHashId,
    enterCode: props.enterCode,
    shouldIncludeAnswerPage,
    handleShouldIncludeAnswerPageToggle
  }} data-sentry-element="unknown" data-sentry-component="WorksheetSettingContext" data-sentry-source-file="WorksheetSettingContext.tsx">
      {children}
    </worksheetSettingContext.Provider>;
};
export const useWorksheetSettingContext = () => {
  const context = useContext(worksheetSettingContext);
  if (context == null) {
    throw new Error('WorksheetSettingContext not found');
  }
  return context;
};